import React, { useEffect, useState } from "react";
import { DndContext, closestCorners, pointerWithin, PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors, useDroppable } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import api, { apiError } from "@/lib/api";
import { TaskRow } from "@/components/tasks/TaskRow";
import { GripVertical } from "lucide-react";
import { toast } from "sonner";

const NONE = "__none__";

function SortableTask({ t, canDrag, onChanged, backTo, templateMode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: t.id, disabled: !canDrag });
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
      className="flex items-stretch bg-white border-b border-slate-100 last:border-0">
      {canDrag && <button type="button" {...attributes} {...listeners} aria-label="Drag to reorder" data-testid={`task-drag-${t.id}`}
        className="cursor-grab touch-none px-1.5 text-slate-300 hover:text-slate-500"><GripVertical className="w-4 h-4" /></button>}
      <div className="flex-1 min-w-0"><TaskRow task={t} onChanged={onChanged} showAssignee backTo={backTo} showVisibility templateMode={templateMode} /></div>
    </div>
  );
}

function Group({ gkey, title, ids, byId, canDrag, onChanged, backTo, templateMode }) {
  const { setNodeRef, isOver } = useDroppable({ id: gkey });
  if (!ids.length && !canDrag) return null;
  return (
    <div data-testid={`task-group-${gkey === NONE ? "none" : gkey}`}>
      <p className="text-xs font-semibold text-slate-600 mb-1">{title} <span className="text-slate-400">· {ids.length}</span></p>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div ref={setNodeRef} className={`rounded-xl border border-slate-200 shadow-sm overflow-hidden min-h-[2.75rem] ${isOver ? "bg-brand/5" : ""}`}>
          {ids.map((id) => byId[id] && <SortableTask key={id} t={byId[id]} canDrag={canDrag} onChanged={onChanged} backTo={backTo} templateMode={templateMode} />)}
          {!ids.length && <p className="px-4 py-3 text-xs text-slate-400 text-center">Drag a task here</p>}
        </div>
      </SortableContext>
    </div>
  );
}

const build = (list, keys) => {
  const g = Object.fromEntries(keys.map((k) => [k, []]));
  list.forEach((t) => { (g[t.milestone_id] || g[NONE]).push(t.id); });
  return g;
};

export function ProjectTaskGroups({ projectId, list, milestones, canDrag, onChanged, backTo, templateMode = false }) {
  const keys = [...milestones.map((m) => m.id), NONE];
  const [groups, setGroups] = useState(() => build(list, keys));
  useEffect(() => { setGroups(build(list, [...milestones.map((m) => m.id), NONE])); }, [list, milestones]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const byId = Object.fromEntries(list.map((t) => [t.id, t]));
  const find = (id) => (id in groups ? id : Object.keys(groups).find((k) => groups[k].includes(id)));
  const collide = (args) => {
    const hit = pointerWithin(args).find((c) => c.id in groups && !groups[c.id].filter((x) => x !== args.active.id).length);
    return hit ? [hit] : closestCorners(args);
  };
  const over = ({ active, over: o }) => {
    if (!o) return;
    const from = find(active.id), to = find(o.id);
    if (!from || !to || from === to) return;
    setGroups((g) => {
      const dest = g[to].filter((x) => x !== active.id);
      const idx = o.id in g ? dest.length : Math.max(0, dest.indexOf(o.id));
      dest.splice(idx, 0, active.id);
      return { ...g, [from]: g[from].filter((x) => x !== active.id), [to]: dest };
    });
  };
  const end = async ({ active, over: o }) => {
    const to = find(active.id);
    if (!to) return;
    let ids = groups[to];
    if (o && !(o.id in groups) && ids.includes(o.id) && o.id !== active.id) ids = arrayMove(ids, ids.indexOf(active.id), ids.indexOf(o.id));
    setGroups((g) => ({ ...g, [to]: ids }));
    const ms = to === NONE ? null : to;
    try {
      await api.post(`/projects/${projectId}/tasks/reorder`, { milestone_id: ms, ids });
      toast.success((byId[active.id]?.milestone_id || null) !== ms ? "Task moved to milestone" : "Task order saved");
    } catch (e) { toast.error(apiError(e)); }
    onChanged();
  };
  const titles = { ...Object.fromEntries(milestones.map((m) => [m.id, m.title])), [NONE]: "No Milestone" };
  return (
    <DndContext sensors={sensors} collisionDetection={collide} onDragOver={over} onDragEnd={end}>
      <div className="space-y-4" data-testid="project-task-groups">
        {keys.map((k) => <Group key={k} gkey={k} title={titles[k]} ids={groups[k] || []} byId={byId} canDrag={canDrag} onChanged={onChanged} backTo={backTo} templateMode={templateMode} />)}
      </div>
    </DndContext>
  );
}
